import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Plus, Send, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useJobPackets } from '@/hooks/use-job-packets';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { JobPacketStatus } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<JobPacketStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-yellow-100 text-yellow-800',
  acknowledged: 'bg-green-100 text-green-800',
};

const statusIcons: Record<JobPacketStatus, typeof Package> = {
  draft: Package,
  sent: Send,
  viewed: Mail,
  acknowledged: CheckCircle,
};

export function JobPacketsPage() {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const timeZone = useOrgTimeZone();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobPacketStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useJobPackets({
    page,
    page_size: 20,
    q: search || undefined,
    status: statusFilter || undefined,
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Package className="h-7 w-7 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Job Packets</h1>
          </div>
          <p className="text-gray-600 mt-1">
            Manage and send job information packets to techs via H3Kit
          </p>
        </div>
        {canManageCrew && (
          <Button asChild>
            <Link to="/crew/assignments/new">
              <Plus className="h-4 w-4 mr-2" />
              New Assignment
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Search by tech or project..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="sm:max-w-xs"
            />
            <Select
              value={statusFilter || 'all'}
              onValueChange={(v) => {
                setStatusFilter(v === 'all' ? '' : v as JobPacketStatus | '');
                setPage(1);
              }}
            >
              <SelectTrigger className="sm:max-w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="viewed">Viewed</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Packets List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {data ? `${data.count} packet${data.count !== 1 ? 's' : ''}` : 'Loading...'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-gray-500 text-center py-8">Loading job packets...</p>
          ) : !data || data.results.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No job packets found.</p>
              <p className="text-sm text-gray-400 mt-1">
                Create a job packet from an assignment to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.results.map((packet) => {
                const StatusIcon = statusIcons[packet.status];
                return (
                  <Link
                    key={packet.id}
                    to={`/crew/job-packets/${packet.id}`}
                    className="block"
                  >
                    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <StatusIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {packet.assignment_display}
                          </p>
                          <p className="text-sm text-gray-600">
                            Tech: {packet.tech_name} &middot; Created{' '}
                            {formatDateTimeInTimeZone(packet.created_at, timeZone, { dateStyle: 'medium' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {packet.sent_via && (
                          <span className="text-xs text-gray-400">
                            via {packet.sent_via}
                          </span>
                        )}
                        <Badge className={statusColors[packet.status]}>
                          {packet.status}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {data && data.count > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={!data.previous}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600 self-center">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.next}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
